import { PaymentStatus } from "@/app/@types/enums";
import { JobV2 } from "@/app/@types/job";
import { Flex, Text } from "@mantine/core";
import { useEffect } from "react";
import PaymentEntryForm from "@/app/_components/Payment/PaymentEntryForm/PaymentEntryForm";
import { useUpdateJob } from "@/app/_hooks/useUpdateJob";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { authenticatedFetch } from "@/app/_utils/authenticatedFetch";
import { calculateJob, nearZero } from "@/app/_helpers/math";
import useFormatCurrency from "@/app/_hooks/useFormatCurrency";

interface TakeJobPaymentFormProps {
  job: JobV2;
  onDone: () => void;
  onCancel: () => void;
  // See PaymentEntryForm: host modal provides a bounded flex-column body.
  fillHeight?: boolean;
}

/**
 * The full "take a payment against a job" flow: PaymentEntryForm wired to
 * recalculate job totals and persist via useUpdateJob, with success/error
 * alerts. Shared by the jobs board Take Payment modal (JobActions) and the
 * POS trigger's "On Job" tab.
 */
const TakeJobPaymentForm = ({
  job,
  onDone,
  onCancel,
  fillHeight = false,
}: TakeJobPaymentFormProps) => {
  const user = useUserContext();
  const setAlert = useSetAlert();
  const formatCurrency = useFormatCurrency();

  const {
    mutate: updateJobV2,
    isSuccess: isUpdateJobSuccess,
    isError: isUpdateJobError,
    isPending: isUpdateJobPending,
    error: updateJobError,
    data: updatedJobData,
  } = useUpdateJob();

  useEffect(() => {
    if (isUpdateJobSuccess) {
      setAlert({
        type: "success",
        title: "Payment successfully taken!",
        content: (
          <Flex direction="column" gap="xs">
            <Text>
              Payment of{" "}
              {formatCurrency(
                updatedJobData.updatedJob.payments[
                  updatedJobData.updatedJob.payments.length - 1
                ].total,
                job.client.currency
              )}{" "}
              taken for Job #{job.jobId}.
            </Text>
          </Flex>
        ),
      });
      // Close only after the update lands. Closing immediately (the old
      // behavior) unmounted this form before this effect ran, so neither
      // the success nor the error alert ever showed from the row menu.
      onDone();
    }

    if (isUpdateJobError) {
      setAlert({
        type: "error",
        title: "Something went wrong taking the payment.",
        content: (
          <Flex direction="column" gap="xs">
            <Text size="sm">
              We could not update the job after taking the payment
            </Text>
            <Text size="sm">{updateJobError.message}</Text>
            <Text size="sm">Support has been notified!</Text>
          </Flex>
        ),
      });

      authenticatedFetch("/api/email/errorNotification", {
        method: "POST",
        body: JSON.stringify({
          userName: user.settings.name,
          userId: user.uid,
          error:
            updateJobError.message +
            `jobId: ${job.jobId} UserId: ${user.uid}
            ${JSON.stringify(job)}`,
          location: "TakeJobPaymentForm",
        }),
      });
    }
  }, [isUpdateJobSuccess, isUpdateJobError]);

  return (
    <PaymentEntryForm
      currency={job.client.currency}
      job={{
        ...job,
        paymentStatus:
          !nearZero(job.totalDeposit) &&
          job.paymentStatus === PaymentStatus.AwaitingDeposit
            ? PaymentStatus.DepositReceived
            : PaymentStatus.PartiallyPaid,
      }}
      withoutJobTitle={true}
      fillHeight={fillHeight}
      onSubmit={(paymentResult) => {
        const jobToUpdate: JobV2 = {
          ...job,
          paymentStatus:
            !nearZero(job.totalDeposit) &&
            job.paymentStatus === PaymentStatus.AwaitingDeposit
              ? PaymentStatus.AwaitingDeposit
              : PaymentStatus.PartiallyPaid,
          lineItems: paymentResult.newProcessingFeeLineItem
            ? [...job.lineItems, paymentResult.newProcessingFeeLineItem]
            : job.lineItems,
          payments: paymentResult.updatedPayments,
        };

        const lineItems = jobToUpdate.lineItems;
        const payments = jobToUpdate.payments || [];
        const includeTax = jobToUpdate.includeTax;

        // Calculate effective tax rate - use 0 if tax is not included
        const effectiveTaxRate = includeTax ? jobToUpdate.taxRate || 0 : 0;

        /**
         * Calculate all financial totals using our precision-safe math helpers
         * Each calculation returns a DecimalValue that maintains precision
         * We use .raw() to convert to number for form storage
         */

        const calculations = {
          // Basic totals
          subtotal: calculateJob.subtotal(lineItems).raw(),
          totalDiscount: calculateJob.totalDiscount(lineItems).raw(),
          subtotalAfterDiscounts: calculateJob
            .subtotalAfterDiscounts(lineItems)
            .raw(),

          // Tax calculations
          totalTax: calculateJob
            .tax(lineItems, effectiveTaxRate, includeTax)
            .raw(),

          // Final totals
          grandTotal: calculateJob
            .grandTotal(lineItems, effectiveTaxRate, includeTax)
            .raw(),

          // Deposit and balance
          totalDeposit: jobToUpdate.totalDeposit,

          remainingBalance: calculateJob
            .remainingBalance(lineItems, effectiveTaxRate, payments, includeTax)
            .raw(),
        };

        jobToUpdate.subtotal = calculations.subtotal;
        jobToUpdate.totalDiscount = calculations.totalDiscount;
        jobToUpdate.subtotalAfterDiscounts = calculations.subtotalAfterDiscounts;

        jobToUpdate.totalTax = calculations.totalTax;
        jobToUpdate.grandTotal = calculations.grandTotal;
        jobToUpdate.totalDeposit = calculations.totalDeposit;
        jobToUpdate.remainingBalance = calculations.remainingBalance;
        jobToUpdate.paymentStatus = paymentResult.newPaymentStatus;
        updateJobV2({
          updateJobFormData: jobToUpdate,
          job,
        });
      }}
      onCancel={onCancel}
      paymentStatusLabelOverride={job.paymentStatus}
      isFinalizing={isUpdateJobPending}
    />
  );
};

export default TakeJobPaymentForm;
